/**
 * SheetScanCard — the "snap the printed sheet instead of typing" accelerator
 * that sits above an entry grid (PROD-1279 D25, plan 03 §C).
 *
 * Generalised out of the chef's `TempSheetUpload`, which is now a thin adapter
 * over this component (`temp-sheet-upload.tsx`), so the chef temp-log page and
 * the two school tasks run ONE card. Two shapes:
 *
 *  - single-photo, photo-persisting (chef): `multiple` off, so choosing a file
 *    reads it straight away — there is no queue to review — and a stored
 *    `photo` renders the persistent "Uploaded from picture" banner with a
 *    full-image dialog.
 *  - multi-photo (school transport / service): up to six pages are queued with
 *    thumbnails, and one `Read sheet` press sends them all in a single request.
 *
 * Presentational + local UI state only. The caller owns extraction and applying
 * the numbers: `onExtract(files)` resolves with a summary to display, or throws
 * with a user-readable message. Nothing is submitted here — every filled cell
 * is for review, which is why the copy ends "Double-check them, then confirm."
 *
 * Camera affordance: `md:hidden` OR `useIsMobile()`. The CSS half keeps phones
 * covered without JS; the hook half is what gets an iPad or a touch laptop the
 * button, since those are wide. Layout itself never branches in JS (D12).
 */

import { Button } from '@ordo/ui/button'
import { cn } from '@ordo/ui/cn'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ordo/ui/dialog'
import { useIsMobile } from '@ordo/ui/use-is-mobile'
import { AlertTriangle, Camera, CheckCircle2, ImageIcon, Loader2, Upload, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useStoredPhotoUrl } from '@/hooks/use-stored-photo-url'
import type { TempLogPhotoMeta } from '@/lib/chefs/temp-log-photos'
import type { ScanPartialSummary } from '@/lib/sheet-scan/apply-result'

/** What the caller reports back after applying an extraction to its grid. */
export interface ScanApplySummary extends ScanPartialSummary {
  /** Cells in the task (filled + still empty). */
  totalCount: number
  /** Whether the sheet's time reading prefilled the task's Time reading input. */
  timeReadingApplied: boolean
}

/** Every string that differs between the chef card and the school cards. */
export interface SheetScanCopy {
  /** Title in `idle` and `error`. */
  idleTitle: string
  idleBodyMobile: string
  idleBodyDesktop: string
  /** Title once a scan resolves. */
  doneTitle: string
  /** Unit inside "Filled {n} of {m} {filledUnit}." */
  filledUnit: string
  /** Body when `onExtract` rejects without a message. */
  errorFallback: string
  /** Camera button before a scan has run. */
  takeLabel: string
  /** Upload button before a scan has run. */
  uploadLabel: string
  /** Upload button while photos are queued (multi-photo only). */
  addLabel: string
  /** Primary that sends the queued photos (multi-photo only). */
  readLabel: string
  /** Camera button once a scan has resolved. */
  retakeLabel: string
  /** Upload button once a scan has resolved. */
  uploadAgainLabel: string
  /** Single-photo only: the persisted-photo banner + its dialog. Absent ⇒ neither renders. */
  photoView?: {
    title: string
    viewLabel: string
    dialogTitle: string
    /** Body when the stored photo's bytes are gone. */
    missing: string
  }
}

export interface SheetScanCardProps {
  copy: SheetScanCopy
  /** Extract and apply; resolves with a summary, or throws a user-readable message. */
  onExtract: (files: File[]) => Promise<ScanApplySummary>
  /** Queue several pages behind one `Read sheet` press. Default false. */
  multiple?: boolean
  /** Queue ceiling; clamped to 1..6. Defaults to 6 when `multiple`, else 1. */
  maxPhotos?: number
  disabled?: boolean
  /** Single-photo only: live grid counts drive the banner instead of the returned summary. */
  liveSummary?: { filledCount: number; totalCount: number } | null
  /** Single-photo only: the persisted sheet photo and its loader. */
  photo?: TempLogPhotoMeta | null
  loadPhotoUrl?: (photoId: string) => Promise<string | null>
  /**
   * Merged onto the card's own classes (tailwind-merge, so `mb-0` beats the
   * default `mb-5`). Lets a host that lays the card out itself own the gap.
   */
  className?: string
}

type Phase = 'idle' | 'picking' | 'reading' | 'done' | 'partial' | 'error'

const MAX_PHOTOS_CEILING = 6

/** Copy shared by every mount of the card — the pieces the chef and the schools say identically. */
const READING_BODY = 'This takes a few seconds.'
const PICKING_BODY = 'Add another page, or read the sheet now.'

/** The school flows' strings (plan 03 §H). Exported for the two wizards. */
export const SCHOOL_SHEET_SCAN_COPY: SheetScanCopy = {
  idleTitle: 'Have the printed sheet filled out?',
  idleBodyMobile: 'Snap photos of the sheet and we’ll fill these in for you to review.',
  idleBodyDesktop: 'Upload photos of the sheet and we’ll fill these in for you to review.',
  doneTitle: 'Filled from your photos',
  filledUnit: 'fields from your photos',
  errorFallback: 'Could not read the photos. Try again or enter the numbers by hand.',
  takeLabel: 'Take photo',
  uploadLabel: 'Upload',
  addLabel: 'Add photo',
  readLabel: 'Read sheet',
  retakeLabel: 'Scan again',
  uploadAgainLabel: 'Scan again',
}

function photoKey(file: File): string {
  return `${file.name}:${file.size}`
}

function uploadedAtLabel(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) {
    return ''
  }
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

function readingTitle(count: number): string {
  return count > 1 ? `Reading ${count} photos…` : 'Reading the sheet…'
}

function pickingTitle(count: number): string {
  return count === 1 ? '1 photo ready' : `${count} photos ready`
}

function reviewLine(count: number): string {
  return count === 1 ? '1 value needs a second look.' : `${count} values need a second look.`
}

export function SheetScanCard({
  copy,
  onExtract,
  multiple = false,
  maxPhotos,
  disabled = false,
  liveSummary = null,
  photo = null,
  loadPhotoUrl,
  className,
}: SheetScanCardProps) {
  const isMobile = useIsMobile()
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const uploadInputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<File[]>([])
  const [phase, setPhase] = useState<Phase>('idle')
  const [summary, setSummary] = useState<ScanApplySummary | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [pickError, setPickError] = useState<string | null>(null)
  const [viewOpen, setViewOpen] = useState(false)
  // Load the stored image while the dialog is open; revoked when it closes
  // (or the photo changes / the component unmounts).
  const { url: viewUrl, missing: viewMissing } = useStoredPhotoUrl(photo?.photoId ?? null, loadPhotoUrl, viewOpen)

  const allowMultiple = multiple
  const photoLimit = Math.min(MAX_PHOTOS_CEILING, Math.max(1, maxPhotos ?? (allowMultiple ? MAX_PHOTOS_CEILING : 1)))
  // With a one-photo ceiling there is no queue to review, so picking a file IS
  // the read — the chef card's behaviour, preserved byte for byte.
  const autoRead = photoLimit === 1

  /** Object URLs for the queued photos, revoked whenever the queue changes or the card unmounts. */
  const previews = useMemo(
    () => (allowMultiple ? files.map((file) => ({ key: photoKey(file), url: URL.createObjectURL(file) })) : []),
    [allowMultiple, files],
  )
  useEffect(() => {
    return () => {
      for (const preview of previews) {
        URL.revokeObjectURL(preview.url)
      }
    }
  }, [previews])

  async function runExtract(list: File[]): Promise<void> {
    if (list.length === 0) {
      return
    }
    setPhase('reading')
    setErrorMessage(null)
    setPickError(null)
    try {
      const result = await onExtract(list)
      setSummary(result)
      const needsReview =
        result.lowConfidenceCount > 0 || result.warnings.length > 0 || result.unmatchedRowsSeen.length > 0
      setPhase(needsReview ? 'partial' : 'done')
      // A second scan starts fresh; the photos themselves are never kept (D22).
      setFiles([])
    } catch (err) {
      setErrorMessage(err instanceof Error && err.message ? err.message : copy.errorFallback)
      setSummary(null)
      setPhase('error')
      if (autoRead) {
        setFiles([])
      }
    }
  }

  function addFiles(incoming: File[]): void {
    const base = allowMultiple ? files : []
    const seen = new Set(base.map(photoKey))
    const next = [...base]
    let dropped = false
    for (const file of incoming) {
      const key = photoKey(file)
      if (seen.has(key)) {
        continue
      }
      if (next.length >= photoLimit) {
        dropped = true
        break
      }
      seen.add(key)
      next.push(file)
    }
    setPickError(dropped ? `You can send up to ${photoLimit} photos at a time.` : null)
    setFiles(next)
    if (autoRead) {
      void runExtract(next)
    } else {
      setPhase(next.length === 0 ? 'idle' : 'picking')
    }
  }

  function removeFile(key: string): void {
    const next = files.filter((file) => photoKey(file) !== key)
    setPickError(null)
    setFiles(next)
    setPhase(next.length === 0 ? 'idle' : 'picking')
  }

  const photoView = copy.photoView
  const busy = phase === 'reading' || disabled
  // Transient in-flight states win; otherwise a stored photo renders the
  // persistent uploaded view (the transient `done` only shows when the caller
  // failed to persist the photo — extraction still applied).
  const view: Phase | 'uploaded' =
    phase === 'reading' ? 'reading' : phase === 'error' ? 'error' : photo && photoView ? 'uploaded' : phase

  // Mobile drops the View button — the card itself opens the photo.
  const cardOpensPhoto = isMobile && view === 'uploaded' && !busy
  const scanned = view === 'done' || view === 'partial' || view === 'error' || view === 'uploaded'
  const counts = liveSummary ?? (summary ? { filledCount: summary.filledCount, totalCount: summary.totalCount } : null)
  const storedAt = photo ? uploadedAtLabel(photo.uploadedAt) : ''
  // Both pickers step aside once the queue is full; with no queue they stay put
  // and merely disable, which is what the chef card has always done.
  const pickersHidden = allowMultiple && files.length >= photoLimit

  const title =
    view === 'reading'
      ? readingTitle(files.length)
      : view === 'picking'
        ? pickingTitle(files.length)
        : view === 'uploaded' && photoView
          ? photoView.title
          : view === 'done' || view === 'partial'
            ? copy.doneTitle
            : copy.idleTitle

  const filledLine = counts ? (
    <>
      Filled {counts.filledCount} of {counts.totalCount} {copy.filledUnit}
      {view === 'uploaded' && storedAt ? <> · {storedAt}</> : null}. Double-check them, then confirm.
    </>
  ) : null

  return (
    <>
      {/* The Dialog + hidden inputs live OUTSIDE the clickable section:
          Radix portals the dialog to <body>, but React events still
          bubble through the component tree — nested under the section,
          the close button's click would re-fire the card's open
          handler and the dialog could never close. */}
      <section
        className={cn(
          'mb-5 rounded-[10px] border border-dashed bg-muted/30 p-4',
          cardOpensPhoto && 'cursor-pointer',
          className,
        )}
        role={cardOpensPhoto ? 'button' : undefined}
        tabIndex={cardOpensPhoto ? 0 : undefined}
        aria-label={cardOpensPhoto ? 'View the uploaded temp sheet photo' : undefined}
        onClick={cardOpensPhoto ? () => setViewOpen(true) : undefined}
        onKeyDown={
          cardOpensPhoto
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setViewOpen(true)
                }
              }
            : undefined
        }>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-background text-muted-foreground">
              {view === 'reading' ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : view === 'uploaded' ? (
                <ImageIcon className="h-5 w-5 text-emerald-600" />
              ) : view === 'done' ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              ) : view === 'partial' || view === 'error' ? (
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              ) : (
                <Camera className="h-5 w-5" />
              )}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">{title}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {view === 'reading' ? (
                  READING_BODY
                ) : view === 'picking' ? (
                  PICKING_BODY
                ) : view === 'uploaded' || view === 'done' || view === 'partial' ? (
                  filledLine
                ) : view === 'error' ? (
                  (errorMessage ?? copy.errorFallback)
                ) : (
                  <>
                    {/* Mirror the action set: camera copy on mobile, upload copy on desktop. */}
                    <span className="md:hidden">{copy.idleBodyMobile}</span>
                    <span className="hidden md:inline">{copy.idleBodyDesktop}</span>
                  </>
                )}
              </p>
              {view === 'partial' && summary ? (
                <div className="mt-1 space-y-0.5 text-xs text-amber-700 dark:text-amber-500">
                  {summary.lowConfidenceCount > 0 ? <p>{reviewLine(summary.lowConfidenceCount)}</p> : null}
                  {summary.warnings.map((warning) => (
                    <p key={warning}>{warning}</p>
                  ))}
                  {summary.unmatchedRowsSeen.length > 0 ? (
                    <p>We saw rows on the sheet we could not place: {summary.unmatchedRowsSeen.join(', ')}.</p>
                  ) : null}
                </div>
              ) : null}
              {pickError ? <p className="mt-1 text-xs text-amber-700 dark:text-amber-500">{pickError}</p> : null}
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {/* View is desktop-only — on mobile the card itself opens the photo. */}
            {view === 'uploaded' && photoView && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="hidden rounded-full md:inline-flex"
                disabled={busy}
                onClick={() => setViewOpen(true)}>
                <ImageIcon className="mr-1.5 h-4 w-4" />
                {photoView.viewLabel}
              </Button>
            )}
            {/* Camera capture is a phone affordance — CSS-hidden on desktop,
              where Upload covers picking an existing image, but kept for any
              touch device the hook reports (iPad, touch laptop). With a photo
              stored it slims down to a link-style Retake. */}
            {!pickersHidden && (
              <Button
                type="button"
                variant={view === 'uploaded' ? 'link' : 'outline'}
                size="sm"
                className={[view === 'uploaded' ? 'h-auto p-0' : 'rounded-full', isMobile ? '' : 'md:hidden']
                  .filter(Boolean)
                  .join(' ')}
                disabled={busy}
                onClick={(e) => {
                  e.stopPropagation()
                  cameraInputRef.current?.click()
                }}>
                {view !== 'uploaded' && <Camera className="mr-1.5 h-4 w-4" />}
                {scanned ? copy.retakeLabel : copy.takeLabel}
              </Button>
            )}
            {!pickersHidden && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={view === 'uploaded' ? 'hidden rounded-full md:inline-flex' : 'rounded-full'}
                disabled={busy}
                onClick={(e) => {
                  e.stopPropagation()
                  uploadInputRef.current?.click()
                }}>
                <Upload className="mr-1.5 h-4 w-4" />
                {view === 'picking' ? copy.addLabel : scanned ? copy.uploadAgainLabel : copy.uploadLabel}
              </Button>
            )}
            {allowMultiple && files.length > 0 && (
              <Button
                type="button"
                size="sm"
                className="rounded-full"
                disabled={busy}
                onClick={(e) => {
                  e.stopPropagation()
                  void runExtract(files)
                }}>
                {copy.readLabel}
              </Button>
            )}
          </div>
        </div>

        {previews.length > 0 && (
          <ul className="mt-3 flex flex-wrap gap-2">
            {previews.map((preview, i) => (
              <li key={preview.key} className="relative">
                <img src={preview.url} alt="" className="h-14 w-14 rounded-md border object-cover" />
                <button
                  type="button"
                  aria-label={`Remove photo ${i + 1}`}
                  className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border bg-background text-muted-foreground"
                  disabled={busy}
                  onClick={(e) => {
                    e.stopPropagation()
                    removeFile(preview.key)
                  }}>
                  <X className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Full-image view of the stored sheet photo. */}
      {photoView && (
        <Dialog open={viewOpen} onOpenChange={setViewOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{photoView.dialogTitle}</DialogTitle>
            </DialogHeader>
            {viewUrl ? (
              <img
                src={viewUrl}
                alt="Uploaded temperature sheet"
                className="max-h-[70vh] w-full rounded-lg object-contain"
              />
            ) : (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {viewMissing ? photoView.missing : 'Loading photo…'}
              </p>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Hidden pickers. `capture` hints mobile to open the camera. */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple={allowMultiple}
        className="hidden"
        onChange={(e) => {
          addFiles(Array.from(e.target.files ?? []))
          e.target.value = ''
        }}
      />
      <input
        ref={uploadInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple={allowMultiple}
        className="hidden"
        onChange={(e) => {
          addFiles(Array.from(e.target.files ?? []))
          e.target.value = ''
        }}
      />
    </>
  )
}
